import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { listAccounts } from '../accounts/api';
import type { Account } from '../../types/account';
import type { AudienceScope } from '../../types/broadcast';
import { buildChildrenMap, descendantsOf } from '../../lib/broadcastAudience';
import { AudienceTreeSelect } from './AudienceTreeSelect';
import { AudienceFilters } from './AudienceFilters';

// Stage 6A v2 §1.2 — the audience selection controls (left pane): the
// All-Brands / Specific-Brands toggle, the multi-brand chooser, and the
// cross-brand tree. Filter chips (§3-§5) are appended by AudienceFilters.

type AudiencePanelProps = {
  scope: AudienceScope;
  onChange: (next: AudienceScope) => void;
};

export function AudiencePanel({ scope, onChange }: AudiencePanelProps) {
  const accountsQuery = useQuery({ queryKey: ['accounts'], queryFn: listAccounts });
  const accounts = accountsQuery.data ?? [];

  const brands = useMemo(
    () =>
      accounts
        .filter((a) => a.accountType === 'brand' && (a.status ?? 'active') === 'active')
        .sort((a, b) => a.name.localeCompare(b.name)),
    [accounts],
  );

  const isAll = scope.brandSelection === 'all';
  const brandIds = scope.brandSelection === 'all' ? [] : scope.brandSelection.brandAccountIds;

  const setMode = (mode: 'all' | 'specific') => {
    if (mode === 'all') {
      onChange({ ...scope, brandSelection: 'all', selectedAccountIds: [], excludedAccountIds: [] });
    } else {
      onChange({ ...scope, brandSelection: { brandAccountIds: [] } });
    }
  };

  // Removing a brand drops its whole subtree from the literal selection/exclusion
  // sets (those accounts are no longer in scope).
  const toggleBrand = (id: string) => {
    if (scope.brandSelection === 'all') return;
    const current = scope.brandSelection.brandAccountIds;
    if (current.includes(id)) {
      const childrenByParent = buildChildrenMap(accounts);
      const subtree = new Set<string>([id, ...descendantsOf(id, childrenByParent)]);
      onChange({
        ...scope,
        brandSelection: { brandAccountIds: current.filter((b) => b !== id) },
        selectedAccountIds: scope.selectedAccountIds.filter((a) => !subtree.has(a)),
        excludedAccountIds: scope.excludedAccountIds.filter((a) => !subtree.has(a)),
      });
    } else {
      onChange({ ...scope, brandSelection: { brandAccountIds: [...current, id] } });
    }
  };

  return (
    <div className="space-y-3">
      <fieldset className="flex flex-col gap-1">
        <label className="flex items-center gap-2 text-sm">
          <input type="radio" name="brandMode" checked={isAll} onChange={() => setMode('all')} />
          All active brands
        </label>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="radio"
            name="brandMode"
            checked={!isAll}
            onChange={() => setMode('specific')}
          />
          Specific brands
        </label>
      </fieldset>

      {isAll ? (
        <p className="rounded bg-slate-50 px-3 py-2 text-sm text-slate-600">
          Targeting every active account across all brands. Use the filters below to narrow by
          level, comms purpose, or account manager.
        </p>
      ) : (
        <>
          <div>
            <span className="mb-1 block text-sm font-medium text-slate-700">Brands</span>
            {accountsQuery.isLoading ? (
              <p className="text-sm text-slate-500">Loading brands…</p>
            ) : brands.length === 0 ? (
              <p className="text-sm text-slate-500">No active brands.</p>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {brands.map((b: Account) => {
                  const on = brandIds.includes(b.id);
                  return (
                    <button
                      key={b.id}
                      type="button"
                      onClick={() => toggleBrand(b.id)}
                      className={`rounded-full border px-3 py-1 text-xs ${
                        on
                          ? 'border-slate-900 bg-slate-900 text-white'
                          : 'border-slate-300 text-slate-600 hover:bg-slate-50'
                      }`}
                    >
                      {b.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {brandIds.length > 0 ? (
            <AudienceTreeSelect rootBrandIds={brandIds} scope={scope} onChange={onChange} />
          ) : (
            <p className="text-sm text-slate-500">Choose one or more brands to pick their audience.</p>
          )}
        </>
      )}

      <AudienceFilters scope={scope} onChange={onChange} />
    </div>
  );
}
